/* eslint-disable prettier/prettier */
import { motion, useScroll, useTransform } from "framer-motion";
import { FC, useEffect, useRef, useState } from "react";

import useMediaQuery from "@/hooks/useMediaQuery";
import { MediaBannerHighlightContent, MediaBannerHighlightType } from "@/types";
import cn from "@/utils/cn";

import MediaBannerHighlightCardContent from "./MediaBannerHighlightCardContent";

export interface Props {
  content: MediaBannerHighlightContent;
  animation?: boolean;
  type: MediaBannerHighlightType | string;
  boxAlignment: "left" | "center" | "right" | string;
  parallaxAnimation?: boolean;
  wrappedText?: boolean;
}

const MediaBannerHighlightCard: FC<Props> = ({ content, type, animation, boxAlignment, parallaxAnimation, wrappedText }) => {
  const container = useRef<HTMLDivElement>(null);
  const backgroundContainer = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const gradientBg = content?.gradientColor && content.gradientColor?.value?.replace(/rgb/i, "rgba").replace(/\)/i, ",0.4)");

  const [isPinned, setIsPinned] = useState(false);

  const getSiblings = (elm: any) => elm && elm.parentNode && [...elm.parentNode.children].filter((node) => node != elm);

  useEffect(() => {
    if (container.current && type === MediaBannerHighlightType.FULL_SCREEN && animation) {
      const siblings = getSiblings(container.current);

      if (!isPinned) return;

      siblings.forEach((sibling: HTMLElement) => {
        if (!sibling.classList.contains("fixed") && !sibling.classList.contains("sticky")) {
          sibling.style.setProperty("z-index", "2");
          sibling.style.setProperty("position", "relative");

          if (!sibling.className.includes("bg-") && !sibling.style.backgroundImage && !sibling.style.backgroundColor && animation) {
            sibling.style.setProperty("background-color", "white");
          }
        }
      });
    }
  }, [container, type, animation, isPinned]);

  const { scrollYProgress } = useScroll({
    target: backgroundContainer,
    offset: ["start end", "end start"],
  });

  const boxedY = [-500, 300];
  const fullY = [-500, 500];
  const separatorY = isMobile ? [-500, 300] : [-1000, -300];
  const backgroundPositionY = useTransform(
    scrollYProgress,
    [0, 1],
    type === MediaBannerHighlightType.FULL_SCREEN ? fullY : type === MediaBannerHighlightType.SEPARATOR ? separatorY : boxedY,
  ); // Background scroll effect

  const slowedBackgroundPositionYBoxed = useTransform(backgroundPositionY, (value) => value * (isMobile ? 0.4 : 0.25));
  const slowedBackgroundPositionY = useTransform(backgroundPositionY, (value) => value * (isMobile ? 0.4 : 0.3));

  if (type === MediaBannerHighlightType.FULL_SCREEN) {
    return (
      <motion.div
        className={cn("relative h-lvh", animation && isPinned && "!z-[1]")}
        ref={container}
        onViewportEnter={() => setIsPinned(true)}
        onViewportLeave={() => setIsPinned(false)}
      >
        <div className={cn("w-full h-lvh", animation && isPinned && "fixed top-0 left-0", content.video && "w-dvw")}>
          {content.video ? (
            <video className={cn(`h-full w-full object-cover`)} poster={content.backupVideoImage?.url} muted autoPlay loop playsInline>
              <source src={isDesktop ? content.video.url : content.mobileVideo?.url} />
            </video>
          ) : (
            <motion.div
              ref={backgroundContainer}
              className={cn("h-full w-full bg-cover")}
              style={
                parallaxAnimation
                  ? {
                    backgroundImage: `url(${isDesktop ? content.image?.url : content.mobileImage?.url || content.image?.url}`,
                    backgroundPositionY: slowedBackgroundPositionY,
                  }
                  : {
                    backgroundImage: `url(${isDesktop ? content.image?.url : content.mobileImage?.url || content.image?.url}`,
                    backgroundPosition: "center",
                  }
              }
            ></motion.div>
          )}
          <div
            className={cn(
              "md:w-8\\/24 absolute left-0 md:left-auto md:top-[50%] md:translate-y-[-50%]",
              !wrappedText
                ? "top-0 h-full md:h-auto w-full"
                : "w-22\\/24 top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%] md:translate-x-0",
              boxAlignment === "left" && "md:left-[4.2%]",
              boxAlignment === "right" && "md:right-[4.2%]",
              boxAlignment === "center" && "md:left-[50%] md:translate-x-[-50%]",
            )}
          >
            <MediaBannerHighlightCardContent content={content} type={type} wrappedText={wrappedText} />
          </div>
        </div>
      </motion.div>
    );
  }

  if (type === MediaBannerHighlightType.SEPARATOR) {
    return (
      <div className={cn("relative mx-auto w-full py-m md:py-xxl")}>
        <div className={cn("relative w-full")}>
          <div
            className={cn(
              "w-full aspect-[0.55] md:aspect-[2.5] items-end md:items-center flex  md:px-[4.2%]",
              isDesktop ? "h-[480px]" : "",
              content.primaryCta && wrappedText && isMobile ? "items-center" : "",
              boxAlignment === "left" && "justify-start",
              boxAlignment === "right" && "justify-end",
              boxAlignment === "center" && "justify-center",
            )}
          >
            {content.video ? (
              <video
                className={`w-full object-cover absolute top-0 left-0 h-full overflow-hidden`}
                poster={content.backupVideoImage?.url}
                muted
                autoPlay
                loop
                playsInline
              >
                <source src={isDesktop ? content.video.url : content.mobileVideo?.url} />
              </video>
            ) : (
              <motion.div
                ref={backgroundContainer}
                className={cn("h-full w-full bg-cover absolute top-0 left-0 overflow-hidden", isDesktop ? "h-[480px]" : "")}
                style={
                  parallaxAnimation
                    ? {
                      backgroundImage: `url(${isDesktop ? content.image?.url : content.mobileImage?.url || content.image?.url}`,
                      backgroundPositionY: slowedBackgroundPositionY,
                    }
                    : {
                      backgroundImage: `url(${isDesktop ? content.image?.url : content.mobileImage?.url || content.image?.url}`,
                      backgroundPosition: "center",
                    }
                }
              ></motion.div>
            )}
            <div className={cn("w-full md:w-8\\/24", content.primaryCta && wrappedText && isMobile ? "px-[4.2%]" : "")}>
              <MediaBannerHighlightCardContent content={content} type={type} wrappedText={wrappedText} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div
        className={cn(
          "relative mx-auto w-22\\/24 md:w-20\\/24 x-ray",
          type === MediaBannerHighlightType.BOXED_CAROUSEL ? "py-m md:py-5xl" : "mt-m md:mt-xxl",
        )}
      >
        <style jsx>{`
          .x-ray::after {
            ${content.gradientColor && `background-color:${gradientBg}`};
          }
        `}</style>
        <div className={cn(" relative ")}>
          <div
            className={cn(
              "aspect-[0.55] md:aspect-[2.5] items-end md:items-center flex md:px-[4.2%]",
              boxAlignment === "left" && "justify-start",
              boxAlignment === "right" && "justify-end",
              boxAlignment === "center" && "justify-center",
            )}
          >
            {content.video ? (
              <video
                className={`w-full object-cover absolute top-0 left-0 h-full rounded-2xl overflow-hidden`}
                poster={content.backupVideoImage?.url}
                muted
                autoPlay
                loop
                playsInline
              >
                <source src={isDesktop ? content.video.url : content.mobileVideo?.url} />
              </video>
            ) : (
              <motion.div
                ref={backgroundContainer}
                className={cn("h-full w-full bg-cover absolute top-0 left-0 rounded-2xl overflow-hidden bg-center")}
                style={
                  parallaxAnimation
                    ? {
                      backgroundImage: `url(${isDesktop ? content.image?.url : content.mobileImage?.url || content.image?.url})`,
                      backgroundPositionY: slowedBackgroundPositionYBoxed,
                    }
                    : { backgroundImage: `url(${isDesktop ? content.image?.url : content.mobileImage?.url || content.image?.url}` }
                }
              ></motion.div>
            )}
            <div className={cn("w-full md:w-8\\/24")}>
              <MediaBannerHighlightCardContent content={content} type={type} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaBannerHighlightCard;
